import { useEffect, useRef } from 'react';
import { CheckCircle2, ExternalLink, Landmark } from 'lucide-react';
import { Button } from '../../ui/button';
import type { HiveFundInvestResult } from '../HiveFundInvestFlow.types';
import { formatCurrency } from '../HiveFundInvestFlow.utils';

interface CompleteStepProps {
  result: HiveFundInvestResult;
  onViewPortfolio: () => void;
  onBrowseInvestments: () => void;
}

export function CompleteStep({ result, onViewPortfolio, onBrowseInvestments }: CompleteStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="space-y-8 text-center" role="alert">
      <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-14 h-14 text-foreground" />
      </div>

      <div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-4 focus-visible:outline-none"
        >
          Investment Submitted!
        </h1>
        <p className="text-lg text-muted-foreground">
          Welcome to The Hive Fund. Your journey to passive income begins now.
        </p>
      </div>

      {/* Confirmation details */}
      <div className="rounded-xl border-2 border-border bg-card p-6 text-left">
        <h3 className="text-lg font-medium mb-4">Investment Summary</h3>
        <dl className="divide-y divide-border">
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Confirmation Code</dt>
            <dd className="font-medium font-mono">{result.confirmationCode}</dd>
          </div>
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Amount</dt>
            <dd className="font-medium">{formatCurrency(result.amount)}</dd>
          </div>
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Status</dt>
            <dd className="font-medium">Processing</dd>
          </div>
        </dl>
      </div>

      {/* Wire transfer instructions — investor must send the wire to complete */}
      {result.wireInstructions && (
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6 text-left space-y-4">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-amber-700" />
            <h3 className="text-lg font-medium text-amber-900">Complete Your Wire Transfer</h3>
          </div>
          <p className="text-sm text-amber-800">
            Your investment is reserved. Send a wire for{' '}
            <span className="font-semibold">{formatCurrency(result.amount)}</span> using the details below.
            Your position activates once we receive the funds (typically 1–2 business days).
          </p>
          <dl className="rounded-lg bg-white/70 border border-amber-200 divide-y divide-amber-100 text-sm">
            {result.wireInstructions.bankName && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <dt className="text-muted-foreground">Bank</dt>
                <dd className="font-medium text-foreground text-right">{result.wireInstructions.bankName}</dd>
              </div>
            )}
            {result.wireInstructions.accountName && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <dt className="text-muted-foreground">Account Name</dt>
                <dd className="font-medium text-foreground text-right">{result.wireInstructions.accountName}</dd>
              </div>
            )}
            {result.wireInstructions.accountNumber && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <dt className="text-muted-foreground">Account Number</dt>
                <dd className="font-mono font-medium text-foreground">{result.wireInstructions.accountNumber}</dd>
              </div>
            )}
            {result.wireInstructions.routingNumber && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <dt className="text-muted-foreground">Routing (ABA)</dt>
                <dd className="font-mono font-medium text-foreground">{result.wireInstructions.routingNumber}</dd>
              </div>
            )}
            {result.wireInstructions.swiftCode && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <dt className="text-muted-foreground">SWIFT</dt>
                <dd className="font-mono font-medium text-foreground">{result.wireInstructions.swiftCode}</dd>
              </div>
            )}
            {result.wireInstructions.reference && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-amber-100/60">
                <dt className="text-amber-900 font-medium">Reference / Memo</dt>
                <dd className="font-mono font-semibold text-amber-900">{result.wireInstructions.reference}</dd>
              </div>
            )}
          </dl>
          <p className="text-xs text-amber-800/80">
            Include the reference above so we can match your wire to your account.
          </p>
        </div>
      )}

      {/* Coinbase Commerce payment link for crypto */}
      {result.coinbaseHostedUrl && (
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6 text-left space-y-3">
          <h3 className="text-lg font-medium text-amber-900">Complete Your Crypto Payment</h3>
          <p className="text-sm text-amber-800">
            Your investment is reserved. Click below to complete payment via Coinbase Commerce.
            Your investment will be activated once the transaction is confirmed on-chain.
          </p>
          <a
            href={result.coinbaseHostedUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 w-full justify-center h-12 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Pay with Coinbase Commerce
          </a>
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Button className="h-14 text-lg" onClick={onViewPortfolio}>
          My Portfolio
        </Button>
        <Button variant="outline" className="h-14 text-lg" onClick={onBrowseInvestments}>
          Browse Investments
        </Button>
      </div>
    </div>
  );
}
